// src/store/api/dashboardApi.ts
import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import type {RootState} from "../index";

// Dashboard Stats Types
export interface DashboardStats {
  regions: number;
  townships: number;
  residenceTypes: number;
  residences: number;
  users: number;
  inquiries: number;
  contacts: number;
  visitorStats: {
    today: number;
    yesterday: number;
    thisMonth: number;
    thisYear: number;
    online: number;
  };
}

// Recent Activity Types
export interface ActivityItem {
  id: string;
  type:
    | "property_created"
    | "user_registered"
    | "inquiry_received"
    | "contact_submitted";
  message: string;
  timestamp: string;
  user?: {
    name: string;
    avatar?: string;
  };
  property?: {
    id: number;
    title: string;
  };
}

// Chart Data Types
export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
  }[];
}

export interface PropertyStatsChart {
  monthlyCreated: ChartData;
  typeDistribution: ChartData;
  regionDistribution: ChartData;
}

export interface UserStatsChart {
  monthlyRegistrations: ChartData;
  roleDistribution: ChartData;
}

// Dashboard API Definition
export const dashboardApi = createApi({
  reducerPath: "dashboardApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_URL}/dashboard`,
    prepareHeaders: (headers, {getState}) => {
      const token = (getState() as RootState).auth.token;

      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }

      headers.set("accept", "application/json");
      headers.set("content-type", "application/json");

      return headers;
    },
  }),
  tagTypes: ["DashboardStats", "RecentActivity", "Charts"],
  endpoints: (builder) => ({
    // Get Dashboard Statistics
    getDashboardStats: builder.query<DashboardStats, void>({
      query: () => "/stats",
      providesTags: ["DashboardStats"],
      transformResponse: (response: {data: DashboardStats}) => response.data,
    }),

    // Get Recent Activity
    getRecentActivity: builder.query<ActivityItem[], {limit?: number}>({
      query: ({limit = 10} = {}) => `/activity?limit=${limit}`,
      providesTags: ["RecentActivity"],
      transformResponse: (response: {data: ActivityItem[]}) => response.data,
    }),

    // Get Property Statistics for Charts
    getPropertyStats: builder.query<
      PropertyStatsChart,
      {period?: "week" | "month" | "year"}
    >({
      query: ({period = "month"} = {}) => `/stats/properties?period=${period}`,
      providesTags: ["Charts"],
      transformResponse: (response: {data: PropertyStatsChart}) =>
        response.data,
    }),

    // Get User Statistics for Charts
    getUserStats: builder.query<
      UserStatsChart,
      {period?: "week" | "month" | "year"}
    >({
      query: ({period = "month"} = {}) => `/stats/users?period=${period}`,
      providesTags: ["Charts"],
      transformResponse: (response: {data: UserStatsChart}) => response.data,
    }),

    // Get System Health Status
    getSystemHealth: builder.query<
      {
        database: "healthy" | "warning" | "error";
        storage: "healthy" | "warning" | "error";
        memory: "healthy" | "warning" | "error";
        uptime: string;
        version: string;
      },
      void
    >({
      query: () => "/health",
      transformResponse: (response: any) => response.data,
    }),

    // Refresh dashboard cache
    refreshDashboard: builder.mutation<void, void>({
      query: () => ({
        url: "/refresh",
        method: "POST",
      }),
      invalidatesTags: ["DashboardStats", "RecentActivity", "Charts"],
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetDashboardStatsQuery,
  useGetRecentActivityQuery,
  useGetPropertyStatsQuery,
  useGetUserStatsQuery,
  useGetSystemHealthQuery,
  useRefreshDashboardMutation,
} = dashboardApi;

// Selector functions
export const selectDashboardStats =
  dashboardApi.endpoints.getDashboardStats.select();
export const selectRecentActivity =
  dashboardApi.endpoints.getRecentActivity.select({limit: 10});

// Transform utilities for chart data
export const transformToChartData = (
  data: any[],
  labelKey: string,
  valueKey: string
): ChartData => ({
  labels: data.map((item) => item[labelKey]),
  datasets: [
    {
      label: "Count",
      data: data.map((item) => item[valueKey]),
      backgroundColor: [
        "#3B82F6",
        "#10B981",
        "#F59E0B",
        "#EF4444",
        "#8B5CF6",
        "#06B6D4",
        "#84CC16",
        "#F97316",
      ],
      borderColor: "#374151",
    },
  ],
});

export default dashboardApi;
